import isElectron from 'is-electron';
import { useEffect, useRef } from 'react';
import type { ELanguages } from '../../i18n';
import { addTranslations, getResourceBundle } from '../../i18n';
import {
  PushyBannerState,
  selectAreTranslationsLoading,
  setAreTranslationsLoading,
  setPushyConnectionBanner,
} from './AppSlice';
import Splash from '../../components/Splash/Splash';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { loginUsingSsoCode } from '../Login/LoginSlice/actionCreators';
import {
  selectIsOnLine,
  setIsAppUpdateRecommended,
} from '../CheckAppAvailable/checkAppAvailableSlice';
import { getItem } from '../../utils/storage';
import { getApiVersions } from '../../apis/apiVersionsAPI';
import type { PushyConnectionResponse } from '../../components/MessageItem/BlockedStateBanner/PushyConnectionBanner';
import { API_VERSION } from '../../config/app';
import { router } from '../../routes/AllRoutes';

export function App({ children }: { children: React.ReactElement }) {
  const areTranslationsLoading = useAppSelector(selectAreTranslationsLoading);
  const isOnline = useAppSelector(selectIsOnLine);
  const prevIsOnlineRef = useRef<boolean>(isOnline);

  const dispatch = useAppDispatch();

  const reloadTranslationResources = async () => {
    const initialLang = getItem('language') as ELanguages;
    const languageResourceBundle = await getResourceBundle(initialLang);
    if (!languageResourceBundle) {
      dispatch(setAreTranslationsLoading(true));
      await addTranslations();
      dispatch(setAreTranslationsLoading(false));
    }
  };

  const onElectronAppStart = async () => {
    if (!isElectron()) {
      return;
    }
    if (!window.electronApi) {
      console.error('preload.ts initialization failed.');
      return;
    }

    document.title = 'Cosafe Desktop App';
    window.electronApi.onSsoAuth(payload =>
      // todo[refactor] improve corrupted url handling: https://github.com/cosafe/web/issues/2387
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dispatch(loginUsingSsoCode(payload.email!, payload.code!))
    );
    window.electronApi.onLoginSuccess(payload => {
      // todo[refactor] improve corrupted url handling: https://github.com/cosafe/web/issues/2387
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const q = `sessionId=${encodeURIComponent(payload.sessionId!)}&refreshToken=${encodeURIComponent(payload.refreshToken!)}&region=${encodeURIComponent(payload.region!)}`;
      router.navigate(`/login?${q}`);
    });

    window.electronApi.onPushyConnectivityChange((payload: PushyConnectionResponse) =>
      dispatch(
        setPushyConnectionBanner(
          payload.isConnected ? PushyBannerState.CONNECTED : PushyBannerState.NOT_CONNECTED
        )
      )
    );

    const { data: apiVersions, error } = await getApiVersions();
    if (error) {
      console.log('Calling /core/v1/api-versions failed:', error);
      return;
    }
    dispatch(
      setIsAppUpdateRecommended(
        API_VERSION <= apiVersions.deprecated && API_VERSION >= apiVersions.minimum
      )
    );
  };

  useEffect(() => {
    reloadTranslationResources();
    onElectronAppStart();
  }, []);

  useEffect(() => {
    const wasOffline = prevIsOnlineRef.current === false;
    prevIsOnlineRef.current = isOnline;

    if (!(wasOffline && isOnline)) {
      return;
    }

    reloadTranslationResources();
  }, [dispatch, isOnline]);

  if (areTranslationsLoading) return <Splash />;

  return children;
}
